import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useApiRequest } from '../actions/apiRequest';
import type { Change, UserVoteInfo, StatusOption } from '../types';
import VoteInfoBanner from './VoteInfoBanner';
import TicketCard from './TicketCard';
import FilterBar from './FilterBar';

interface JiraDataListProps {
  data: Change[];
  showVoteInfo?: boolean;
  activeFilter: string;
  fetchData: (filter: string) => Promise<void>;
}

const JiraDataList: React.FC<JiraDataListProps> = ({
  data,
  showVoteInfo = true,
  activeFilter,
  fetchData,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('key-asc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timelineFilter, setTimelineFilter] = useState('all');
  const [myVoteFilter, setMyVoteFilter] = useState('all');
  const [minVotes, setMinVotes] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [userVoteInfo, setUserVoteInfo] = useState<UserVoteInfo | null>(null);
  const [userVoteInfoLoaded, setUserVoteInfoLoaded] = useState(false);
  const apiRequest = useApiRequest();

  const handleStatusFilterChange = (filter: string): void => {
    setStatusFilter(filter);
  };

  const fetchUserVotes = useCallback(async () => {
    try {
      const info = await apiRequest<UserVoteInfo>({ path: '/votes/me' });
      setUserVoteInfo(info ?? null);
      setUserVoteInfoLoaded(true);
    } catch (error) {
      console.error('Error fetching user votes:', error);
      setUserVoteInfoLoaded(true);
    }
  }, [apiRequest]);

  useEffect(() => {
    fetchUserVotes();
  }, [fetchUserVotes]);

  const statusOptions: StatusOption[] = useMemo(() => {
    const uniqueStatuses = [...new Set(data.map((ticket) => ticket.statusName))]
      .filter(Boolean)
      .sort();
    return [
      { value: 'all', label: 'Alle statussen' },
      ...uniqueStatuses.map((status) => ({ value: status, label: status })),
    ];
  }, [data]);

  const handleVoteChange = async (changeKey: string, action: string): Promise<void> => {
    try {
      if (action === 'vote') {
        await apiRequest({ path: `/votes/${changeKey}`, method: 'POST' });
      } else {
        await apiRequest({ path: `/votes/${changeKey}`, method: 'DELETE' });
      }
      await fetchData(activeFilter);
    } catch (error) {
      console.error('Vote error:', error);
    }
  };

  const hasUserVoted = (ticket: Change): boolean => {
    if (!userVoteInfo || !userVoteInfo.email) return false;
    const userEmail = String(userVoteInfo.email).toLowerCase();
    return (
      ticket.votes?.some(
        (vote) => String(vote.userEmail || '').toLowerCase() === userEmail
      ) || false
    );
  };

  const getTimelinePhase = (status: number): string => {
    if (status >= 0 && status <= 149) return 'early';
    if (status >= 150 && status <= 179) return 'preparation';
    if (status >= 180 && status <= 229) return 'implementation';
    if (status >= 230 && status <= 512) return 'closed';
    return 'unknown';
  };

  let filteredData = data.filter((ticket) => {
    const matchesSearch =
      ticket.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (statusFilter !== 'all' && ticket.statusName !== statusFilter) {
      return false;
    }

    if (timelineFilter !== 'all') {
      const ticketPhase = getTimelinePhase(ticket.status);
      if (ticketPhase !== timelineFilter) return false;
    }

    if (myVoteFilter !== 'all') {
      const voted = hasUserVoted(ticket);
      if (myVoteFilter === 'voted' && !voted) return false;
      if (myVoteFilter === 'not-voted' && voted) return false;
    }

    if (minVotes && minVotes !== '') {
      const minVotesNum = parseInt(minVotes, 10);
      if (!isNaN(minVotesNum) && ticket.voteCount < minVotesNum) {
        return false;
      }
    }

    return true;
  });

  filteredData = [...filteredData].sort((a, b) => {
    switch (sortBy) {
      case 'key-asc':
        return a.key.localeCompare(b.key);
      case 'key-desc':
        return b.key.localeCompare(a.key);
      case 'created-asc':
        return new Date(a.created || 0).getTime() - new Date(b.created || 0).getTime();
      case 'created-desc':
        return new Date(b.created || 0).getTime() - new Date(a.created || 0).getTime();
      case 'votes-asc':
        return a.voteCount - b.voteCount;
      case 'votes-desc':
        return b.voteCount - a.voteCount;
      case 'status-asc':
        return a.statusName.localeCompare(b.statusName);
      case 'status-desc':
        return b.statusName.localeCompare(a.statusName);
      case 'timeline-asc':
        return a.status - b.status;
      case 'timeline-desc':
        return b.status - a.status;
      default:
        return 0;
    }
  });

  if (!data || data.length === 0) {
    return (
      <div className="relative">
        <p className="text-neutral-400 text-lg">Geen tickets gevonden.</p>
      </div>
    );
  }

  return (
    <>
      {showVoteInfo && (
        <div className="mb-6">
          <VoteInfoBanner userVoteInfo={userVoteInfo} isLoaded={userVoteInfoLoaded} />
        </div>
      )}

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        sortBy={sortBy}
        onSortChange={setSortBy}
        activeFilter={activeFilter}
        statusFilter={statusFilter}
        statusOptions={statusOptions}
        onStatusFilterChange={handleStatusFilterChange}
        timelineFilter={timelineFilter}
        onTimelineFilterChange={setTimelineFilter}
        myVoteFilter={myVoteFilter}
        onMyVoteFilterChange={setMyVoteFilter}
        minVotes={minVotes}
        onMinVotesChange={setMinVotes}
        showAdvanced={showAdvanced}
        onToggleAdvanced={() => setShowAdvanced(!showAdvanced)}
      />
      <p className="mt-4 text-neutral-600 text-sm">
        Gevonden {filteredData.length}/{data.length}{' '}
        {filteredData.length === 1 ? 'resultaat' : 'resultaten'}
      </p>
      <div className="mt-8 grid grid-cols-1 gap-6">
        {filteredData.length > 0 ? (
          filteredData.map((ticket) => (
            <TicketCard
              key={ticket.key}
              ticket={ticket}
              userVoteInfo={userVoteInfo}
              onVoteChange={handleVoteChange}
            />
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <p className="text-neutral-400 text-lg">
              Geen tickets gevonden die overeenkomen met je filters.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default JiraDataList;
